import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "../../data/teacher";
import { userRequest } from "../../utils/requestMethods";
import SkeletonCards from "../common/SkeletonCards";
import { Link } from "react-router-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { getClassGroupId, listClassGroups } from "../../utils/classGroupApi";
import { getArmLabel, pluralize, splitArmName } from "../../utils/classGroupHelpers";

const AssignedSubjectsSection = ({ teacher, school }) => {
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [expandedPermissions, setExpandedPermissions] = useState({});
  const [permissionsPopoverId, setPermissionsPopoverId] = useState(null);
  const [classGroups, setClassGroups] = useState([]);
  const groupingEnabled = Boolean(school?.settings?.class_grouping_enabled);
  const armsLower = pluralize(getArmLabel(school), 2).toLowerCase();

  const fetchSubjects = async () => {
    setLoadingSubjects(true);
    try {
      const { data } = await userRequest.get(
        `/teachers/teacher-assigned-subjects/${school?._id}/${teacher._id}?limit=100`,
      );
      setSubjects(data.subjects || []);
      setExpandedPermissions({}); // Reset permissions on fetch
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  useEffect(() => {
    if (school?._id && teacher?._id) {
      fetchSubjects();
    }
  }, [school?._id, teacher?._id]);

  // Only for class level names on grouped cards; arm names are the fallback.
  useEffect(() => {
    if (!groupingEnabled || !school?._id) return;
    let cancelled = false;
    listClassGroups(school._id)
      .then((groups) => {
        if (!cancelled) setClassGroups(Array.isArray(groups) ? groups : []);
      })
      .catch((err) => console.error("Error fetching class groups:", err));
    return () => {
      cancelled = true;
    };
  }, [groupingEnabled, school?._id]);

  useEffect(() => {
    const closePopoverOnOutsideClick = (event) => {
      if (!event.target.closest("[data-permissions-popover]")) {
        setPermissionsPopoverId(null);
      }
    };

    if (permissionsPopoverId !== null) {
      document.addEventListener("mousedown", closePopoverOnOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", closePopoverOnOutsideClick);
    };
  }, [permissionsPopoverId]);

  const getRoleBadge = (role) => {
    const roleColors = {
      primary: "bg-blue-100 text-blue-800 border-blue-200",
      assistant: "bg-purple-100 text-purple-800 border-purple-200",
      coordinator: "bg-green-100 text-green-800 border-green-200",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
          roleColors[role] || "bg-gray-100 text-gray-800 border-gray-200"
        }`}
      >
        {role?.charAt(0).toUpperCase() + role?.slice(1) || "Teacher"}
      </span>
    );
  };

  const formatPermission = (permission) => {
    return permission
      ?.split("_")
      ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      ?.join(" ");
  };

  const togglePermissions = (subjectId) => {
    setExpandedPermissions((prev) => ({
      ...prev,
      [subjectId]: !prev[subjectId],
    }));
  };

  // The key keeps each card's (or each arm's) expand/popover state separate.
  const renderPermissions = (permissions, subjectId) => {
    if (!permissions || permissions.length === 0) return null;
    return (
      <div className="mt-2 mb-3 relative" data-permissions-popover>
        <button
          onClick={() => togglePermissions(subjectId)}
          className="flex items-center justify-between w-full text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
        >
          <span>Permissions ({permissions.length}):</span>
          {expandedPermissions[subjectId] ? (
            <FiChevronUp className="w-3 h-3" />
          ) : (
            <FiChevronDown className="w-3 h-3" />
          )}
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            expandedPermissions[subjectId]
              ? "max-h-20 opacity-100 mt-2"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex flex-wrap gap-1 relative">
            {permissions.slice(0, 3).map((permission, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
              >
                {formatPermission(permission)}
              </span>
            ))}
            {permissions.length > 3 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPermissionsPopoverId(
                    permissionsPopoverId === subjectId ? null : subjectId,
                  );
                }}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200 transition-colors"
              >
                +{permissions.length - 3} more
              </button>
            )}
          </div>
        </div>
        {permissionsPopoverId === subjectId && (
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-20 max-h-64 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-700">
                All Permissions ({permissions.length})
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPermissionsPopoverId(null);
                }}
                className="text-slate-400 hover:text-slate-600 text-xs"
              >
                ×
              </button>
            </div>
            <div className="flex flex-wrap gap-1">
              {permissions.map((permission, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                >
                  {formatPermission(permission)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // A subject the teacher teaches in several arms of one class level (same shared subject)
  // is one card listing those arms, instead of one card per arm.
  const subjectCards = useMemo(() => {
    const cards = [];
    const byKey = new Map();
    subjects.forEach((subject, index) => {
      const groupId = groupingEnabled ? getClassGroupId(subject.class?.class_group_id) : null;
      const key =
        groupId && subject.subject_ref_id
          ? `${groupId}_${subject.subject_ref_id}`
          : String(subject._id || index);
      if (!byKey.has(key)) {
        const card = { key, groupId, sharedSubjectId: subject.subject_ref_id, arms: [] };
        byKey.set(key, card);
        cards.push(card);
      }
      byKey.get(key).arms.push(subject);
    });
    return cards;
  }, [subjects, groupingEnabled]);

  const groupNameFor = (card) =>
    classGroups.find((g) => String(g._id) === String(card.groupId))?.name ||
    splitArmName(card.arms[0]?.class?.class_name)?.base ||
    "this class level";

  const lessonNotesLink = (subject) => ({
    pathname: `/t/subjects/notes/${subject._id}/${subject.class?._id}`,
    state: { subject },
  });

  const samePermissions = (arms) => {
    const first = [...(arms[0]?.teacher_permissions || [])].sort().join("|");
    return arms.every((a) => [...(a.teacher_permissions || [])].sort().join("|") === first);
  };

  return (
    <div className="flex flex-wrap items-center mt-6 mb-6">
      {loadingSubjects ? (
        <SkeletonCards cardCount={3} layout="vertical" width="md:w-1/3 w-full mb-2" />
      ) : subjectCards.length > 0 ? (
        subjectCards.map((card) => {
          const subject = card.arms[0];
          const multiArm = card.arms.length > 1;
          const role = subject.teacher_role;
          const permissions = subject.teacher_permissions || [];
          const subjectId = card.key;
          const classData = subject.class || {};

          return (
            <div key={subjectId} className="pr-3 md:w-1/3 w-full mb-3">
              <div className="border border-[#C5C5E8] p-3 rounded-2xl">
                <div className="bg-[#EEEEFC] flex items-center justify-center rounded-2xl px-4 py-14">
                  <img src={Icons.book} alt="Subject Icon" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mt-3">
                    <p className="text-sm font-semibold">
                      {subject.subject_name || "Subject Name"}
                    </p>
                    {multiArm && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 uppercase tracking-wider">
                        {card.arms.length} {armsLower}
                      </span>
                    )}
                  </div>

                  {multiArm ? (
                    <>
                      <p className="text-xs mt-2 text-slate-600">{groupNameFor(card)}</p>
                      <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
                        {card.arms.map((arm) => (
                          <li key={arm._id} className="px-2.5 py-2 text-xs">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-slate-800">
                                {arm.class?.class_name || "Class"}
                              </span>
                              <div className="flex items-center gap-2">
                                {arm.teacher_role && getRoleBadge(arm.teacher_role)}
                                {arm.class?._id && (
                                  <Link
                                    to={lessonNotesLink(arm)}
                                    className="text-[#4543B5] font-semibold hover:underline"
                                  >
                                    Notes
                                  </Link>
                                )}
                              </div>
                            </div>
                            {!samePermissions(card.arms) &&
                              renderPermissions(arm.teacher_permissions, `${subjectId}_${arm._id}`)}
                          </li>
                        ))}
                      </ul>
                      {samePermissions(card.arms) && renderPermissions(permissions, subjectId)}
                    </>
                  ) : (
                    <>
                      <p className="text-xs mt-2 text-slate-600">{classData.class_name || "Class"}</p>
                      {subject.lesson_note && (
                        <p className="text-xs mt-1 text-slate-500">
                          {subject.lesson_note.length || 0} Lesson Notes
                        </p>
                      )}

                      {/* Role Badge */}
                      {role && (
                        <div className="flex items-center gap-2 mt-2 mb-2">{getRoleBadge(role)}</div>
                      )}

                      {renderPermissions(permissions, subjectId)}
                    </>
                  )}
                </div>
                {multiArm && card.groupId && card.sharedSubjectId ? (
                  <Link
                    to={{
                      pathname: "/t/tests",
                      state: {
                        createTest: { groupId: card.groupId, sharedSubjectId: card.sharedSubjectId },
                      },
                    }}
                    className="w-full mt-3 sm:pl-2 pl-0 flex items-center justify-end"
                  >
                    <button className="w-full bg-[#4543B5] hover:bg-indigo-900 text-white font-semibold text-xs sm:px-10 px-2 py-2 rounded-full flex items-center justify-center">
                      Create test for all {armsLower}
                    </button>
                  </Link>
                ) : (
                  classData._id && (
                    <Link
                      to={lessonNotesLink(subject)}
                      className="w-full mt-2 sm:pl-2 pl-0 flex items-center justify-end"
                    >
                      <button className="w-full bg-[#4543B5] hover:bg-indigo-900 text-white font-semibold text-xs sm:px-10 px-2 py-2 rounded-full flex items-center justify-center">
                        View Lesson Notes
                      </button>
                    </Link>
                  )
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center w-full text-gray-500">
          No subjects assigned to this teacher yet.
        </div>
      )}
    </div>
  );
};

export default AssignedSubjectsSection;
